import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../lib/api";
import { guardCreate } from "../context/DuplicateGuard";
import { ANAG_TYPES, PRATICA_VISIBLE_FIELDS, DEFAULT_PRATICA_FIELDS } from "../lib/anagConfig";
import TitoloInput from "./TitoloInput";
import { withCfSesso } from "../lib/cfSesso";
import AutocompleteInput from "./AutocompleteInput";
import AddressAutocomplete from "./AddressAutocomplete";
import CompagnieMultiSelect from "./CompagnieMultiSelect";
import IspettoratiCascadeSelect from "./IspettoratiCascadeSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import MultiCellulare from "./MultiCellulare";
import MultiEmail from "./MultiEmail";
import HistoryInput from "./HistoryInput";
import ResizableNote from "./ResizableNote";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, Check, ChevronDown, ChevronRight, GripVertical, X, Plus } from "lucide-react";
import { toast } from "sonner";
import CollaboratoriEditor from "./CollaboratoriEditor";
import SimilarContacts from "./SimilarContacts";

const CAMPI_BY_KEY = Object.fromEntries(PRATICA_VISIBLE_FIELDS.map((f) => [f.key, f]));
const PARTE_FLAG = {
  periti: { key: "perito_di_parte", label: "Perito di parte" },
  medici_legali: { key: "medico_legale_di_parte", label: "Medico legale di parte" },
  avvocati: { key: "avvocato_di_parte", label: "Avvocato di parte" },
};

export default function NuovoProfessionistaDialog({ open, atype, label, initialName, editRec, preset, onSaved, onCancel }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [mainOpen, setMainOpen] = useState(true);
  const cat = ANAG_TYPES.find((t) => t.key === atype);
  const catLabel = label || cat?.label || atype || "";
  const [pool, setPool] = useState({});
  useEffect(() => {
    if (!open || atype !== "liquidatori") return;
    api.get("/anagrafiche/liquidatori").then(({ data }) => {
      const out = {};
      (data || []).forEach((r) => Object.entries(r || {}).forEach(([k, v]) => { if (typeof v === "string" && v.trim()) (out[k] = out[k] || []).push(v.trim()); }));
      setPool(out);
    }).catch(() => setPool({}));
  }, [open, atype]);

  useEffect(() => {
    if (!open) return;
    if (editRec) { setForm({ ...editRec, campi_pratica: Array.isArray(editRec.campi_pratica) ? editRec.campi_pratica : [...DEFAULT_PRATICA_FIELDS] }); return; }
    const nm = (initialName || "").trim();
    const parts = nm.split(" ");
    setForm({ titolo: "nessuno", cognome: parts[0] || "", nome: parts.slice(1).join(" ") || "", compagnie: [], campi_pratica: [...DEFAULT_PRATICA_FIELDS], ...(preset || {}) });
  }, [open, editRec, initialName, preset]);

  const upd = (k) => (v) => setForm((f) => withCfSesso(f, k, v));
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleCampo = (k) => setForm((f) => {
    const cur = Array.isArray(f.campi_pratica) ? f.campi_pratica : DEFAULT_PRATICA_FIELDS;
    return cur.includes(k)
      ? { ...f, campi_pratica: cur.filter((x) => x !== k) }
      : { ...f, campi_pratica: [...cur, k] };
  });
  const dragIdx = useRef(null);
  const onDropAt = (to) => setForm((f) => {
    const from = dragIdx.current; dragIdx.current = null;
    const cur = [...(Array.isArray(f.campi_pratica) ? f.campi_pratica : DEFAULT_PRATICA_FIELDS)];
    if (from == null || from === to || from < 0 || from >= cur.length) return f;
    const [m] = cur.splice(from, 1); cur.splice(to, 0, m);
    return { ...f, campi_pratica: cur };
  });

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = form;
      let rec;
      if (editRec?.id) { const { data } = await api.patch(`/anagrafiche/${atype}/${editRec.id}`, payload); rec = data; }
      else { const { record } = await guardCreate(atype, payload); rec = record; }
      toast.success(editRec ? "Professionista aggiornato" : "Professionista salvato in anagrafica");
      onSaved?.(rec);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const multi = (k, label, opts = {}) => (
    <MultiEmail value={form[k]} extra={form[`${k}_extra`]} testId={`np-${k}`} label={label} {...opts}
      onChange={(main, ex) => setForm((f) => ({ ...f, [k]: main, [`${k}_extra`]: ex }))} />
  );
  const inp = (k, ph) => (atype === "liquidatori"
    ? <HistoryInput value={form[k]} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} options={pool[k] || []} testId={`np-${k}`} />
    : <Input value={form[k] || ""} onChange={set(k)} placeholder={ph} data-testid={`np-${k}`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />);

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) onCancel?.(); }}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="nuovo-professionista-dialog">
        <DialogHeader><DialogTitle className="font-head">{editRec ? "Modifica" : "Nuovo"} professionista</DialogTitle></DialogHeader>
        <div className="border border-slate-700 rounded-lg overflow-hidden">
          <button type="button" onClick={() => setMainOpen((o) => !o)} data-testid="np-main-toggle" className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-800/60 hover:bg-slate-800 text-left transition-colors">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-200">Dati professionista</span>
            {mainOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          </button>
          {mainOpen && (
          <div className="p-4 grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-slate-300 text-xs">Categoria</Label>
            <Input value={catLabel} disabled data-testid="np-categoria" className="mt-1 h-9 bg-slate-800/40 border-slate-700 text-slate-400" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Titolo</Label>
            <TitoloInput value={form.titolo} onChange={upd("titolo")} testId="np-titolo" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Sesso</Label>
            <Select value={form.sesso || undefined} onValueChange={upd("sesso")}>
              <SelectTrigger data-testid="np-sesso" className="mt-1 h-9 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent className="bg-[#121824] border-slate-700 text-slate-100">{["Maschio", "Femmina"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-slate-300 text-xs">Cognome</Label>{inp("cognome")}</div>
          <div><Label className="text-slate-300 text-xs">Nome</Label>{inp("nome")}</div>
          <div className="sm:col-span-2"><Label className="text-slate-300 text-xs">Ragione Sociale (per aziende)</Label>{inp("denominazione")}</div>
          {!editRec && (
            <div className="sm:col-span-2"><SimilarContacts atype={atype} nome={form.nome} cognome={form.cognome} denominazione={form.denominazione} onSelect={(r) => onSaved?.(r)} onCancel={() => onCancel?.()} testId="np-similar" /></div>
          )}
          <div><Label className="text-slate-300 text-xs">Codice Fiscale</Label>{inp("codice_fiscale")}</div>
          <div><Label className="text-slate-300 text-xs">Partita IVA</Label>{inp("partita_iva")}</div>
          <div className="sm:col-span-2"><Label className="text-slate-300 text-xs">Indirizzo</Label>
            <div className="mt-1"><AddressAutocomplete value={form.indirizzo} onChange={upd("indirizzo")} onPick={(p) => setForm((f) => ({ ...f, indirizzo: p.indirizzo, comune: p.comune, cap: p.cap, provincia: p.provincia }))} city={form.comune} prov={form.provincia} testId="np-indirizzo" /></div>
          </div>
          <div><Label className="text-slate-300 text-xs">Numero Civico</Label>{inp("numero_civico")}</div>
          <div><Label className="text-slate-300 text-xs">CAP</Label>{inp("cap")}</div>
          <div>
            <Label className="text-slate-300 text-xs">Comune</Label>
            <div className="mt-1"><AutocompleteInput source="comuni" value={form.comune || ""} onChange={upd("comune")} onSelectItem={(it) => setForm((f) => ({ ...f, comune: it.nome, provincia: it.provincia, cap: f.cap || it.cap }))} placeholder="Comune…" testId="np-comune" /></div>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Provincia</Label>
            <div className="mt-1"><AutocompleteInput source="province" value={form.provincia || ""} onChange={upd("provincia")} placeholder="Provincia…" testId="np-provincia" /></div>
          </div>
          <div><Label className="text-slate-300 text-xs">Telefono</Label>{atype === "liquidatori" ? multi("telefono", "telefono", { type: "tel", star: false, altLabel: "altro telefono" }) : inp("telefono")}</div>
          <div><Label className="text-slate-300 text-xs">Cellulare</Label><div className="mt-1"><MultiCellulare extra={form.cellulari_extra} onExtra={(v) => setForm((f) => ({ ...f, cellulari_extra: v }))} labels={form.cellulari_etichette} onLabels={(v) => setForm((f) => ({ ...f, cellulari_etichette: v }))} testId="np-cellulare"><Input value={form.cellulare || ""} onChange={set("cellulare")} data-testid="np-cellulare" className="h-9 bg-slate-900/60 border-slate-700" /></MultiCellulare></div></div>
          <div><Label className="text-slate-300 text-xs">E-mail</Label>{atype === "liquidatori" ? multi("email", "email") : inp("email")}</div>
          <div><Label className="text-slate-300 text-xs">PEC</Label>{atype === "liquidatori" ? multi("pec", "PEC") : inp("pec")}</div>
          {atype !== "segnalatori" && (
            <div className="sm:col-span-2"><Label className="text-slate-300 text-xs">Compagnie assicurative associate</Label><CompagnieMultiSelect value={form.compagnie || []} onChange={upd("compagnie")} testId="np-compagnie" /></div>
          )}
          {atype === "segnalatori" && (
            <div className="sm:col-span-2 flex items-center gap-2">
              <Checkbox checked={!!form.solo_iniziali_copertina} onCheckedChange={(v) => upd("solo_iniziali_copertina")(!!v)} data-testid="np-solo-iniziali-copertina" />
              <Label className="text-slate-300 text-xs cursor-pointer" onClick={() => upd("solo_iniziali_copertina")(!form.solo_iniziali_copertina)}>Mostra solo le iniziali nella copertina</Label>
            </div>
          )}
          {PARTE_FLAG[atype] && (
            <div className="sm:col-span-2 flex items-center gap-2">
              <Checkbox checked={!!form[PARTE_FLAG[atype].key]} onCheckedChange={(v) => upd(PARTE_FLAG[atype].key)(!!v)} data-testid={`np-${PARTE_FLAG[atype].key}`} />
              <Label className="text-slate-300 text-xs cursor-pointer" onClick={() => upd(PARTE_FLAG[atype].key)(!form[PARTE_FLAG[atype].key])}>{PARTE_FLAG[atype].label}</Label>
            </div>
          )}
          {atype === "liquidatori" && (
            <div className="sm:col-span-2"><Label className="text-slate-300 text-xs">Ispettorato</Label><IspettoratiCascadeSelect compagnie={form.compagnie || []} value={form.ispettorati || []} onChange={upd("ispettorati")} testId="np-ispettorati" /></div>
          )}
          <div className="sm:col-span-2"><Label className="text-slate-300 text-xs">Nota</Label>{atype === "liquidatori"
            ? <ResizableNote value={form.note} onChange={(v) => setForm((f) => ({ ...f, note: v }))} storageKey="np-liquidatori" testId="np-note" />
            : <Textarea value={form.note || ""} onChange={set("note")} rows={2} data-testid="np-note" className="mt-1 bg-slate-900/60 border-slate-700" />}</div>
          </div>
          )}
        </div>
        {atype !== "liquidatori" && (
          <CollaboratoriEditor value={form.collaboratori || []} onChange={upd("collaboratori")} testId="np-collaboratori" />
        )}
        <div className="border border-slate-700 rounded-lg overflow-hidden" data-testid="np-campi-pratica">
          <div className="px-4 py-2.5 bg-slate-800/60">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-200">Campi visibili in pratica</span>
            <p className="text-[11px] text-slate-400 mt-0.5 normal-case font-normal tracking-normal">Scegli e trascina per ordinare i recapiti mostrati sotto al nominativo di questo contatto in ogni scheda pratica.</p>
          </div>
          <div className="p-4 space-y-3">
            <div className="space-y-1.5" data-testid="np-campi-pratica-attivi">
              {(Array.isArray(form.campi_pratica) ? form.campi_pratica : DEFAULT_PRATICA_FIELDS).length === 0 && (
                <div className="text-xs text-slate-500">Nessun campo selezionato: nessun recapito verrà mostrato in pratica.</div>
              )}
              {(Array.isArray(form.campi_pratica) ? form.campi_pratica : DEFAULT_PRATICA_FIELDS).map((k, i) => {
                const f = CAMPI_BY_KEY[k];
                if (!f) return null;
                return (
                  <div key={k} draggable
                    onDragStart={(e) => { dragIdx.current = i; e.dataTransfer.effectAllowed = "move"; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); onDropAt(i); }}
                    data-testid={`np-campo-attivo-${k}`}
                    className="flex items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-2.5 py-2 cursor-grab active:cursor-grabbing">
                    <GripVertical className="h-4 w-4 text-amber-400/70 shrink-0" />
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-mono shrink-0">{i + 1}</span>
                    <span className="text-xs text-slate-100 flex-1">{f.label}</span>
                    <button type="button" onClick={() => toggleCampo(k)} data-testid={`np-campo-remove-${k}`}
                      className="text-slate-400 hover:text-red-400 transition-colors" title={`Rimuovi ${f.label}`}><X className="h-3.5 w-3.5" /></button>
                  </div>
                );
              })}
            </div>
            {PRATICA_VISIBLE_FIELDS.filter((f) => !(Array.isArray(form.campi_pratica) ? form.campi_pratica : DEFAULT_PRATICA_FIELDS).includes(f.key)).length > 0 && (
              <div>
                <div className="text-[11px] text-slate-400 mb-1.5">Aggiungi campo:</div>
                <div className="flex flex-wrap gap-1.5">
                  {PRATICA_VISIBLE_FIELDS.filter((f) => !(Array.isArray(form.campi_pratica) ? form.campi_pratica : DEFAULT_PRATICA_FIELDS).includes(f.key)).map((f) => (
                    <button key={f.key} type="button" onClick={() => toggleCampo(f.key)} data-testid={`np-campo-add-${f.key}`}
                      className="inline-flex items-center gap-1 text-xs rounded-full border border-slate-600 bg-slate-900/40 text-slate-200 px-2.5 py-1 hover:border-amber-500/60 hover:bg-amber-500/10 transition-colors">
                      <Plus className="h-3 w-3" /> {f.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onCancel?.()} data-testid="np-cancel" className="border-slate-700 text-slate-200 hover:bg-slate-800">Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="np-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Salva</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
